/**
 * Pure clipboard/HTML helpers for the support reply composer's paste handling.
 *
 * These are deliberately kept free of any `platejs` import: `platejs` is an ES
 * module that cannot be `require()`d under Jest, so anything that touches it is
 * untestable in our unit suite. By isolating the DOM-only logic here we can
 * cover the paste-normalization rules (the meat of PRO-957) with real unit
 * tests, while `message-editor.tsx` stays the thin wiring layer that feeds the
 * results into the live editor.
 */

/** Inline-level tags whose markup we preserve verbatim inside a paragraph. */
const INLINE_TAGS = new Set([
  "A",
  "ABBR",
  "B",
  "BDI",
  "BDO",
  "BIG",
  "CITE",
  "CODE",
  "DATA",
  "DEL",
  "DFN",
  "EM",
  "FONT",
  "I",
  "IMG",
  "INS",
  "KBD",
  "MARK",
  "Q",
  "S",
  "SAMP",
  "SMALL",
  "SPAN",
  "STRONG",
  "SUB",
  "SUP",
  "TIME",
  "U",
  "VAR",
  "WBR",
]);

/** The slice of `DataTransfer` paste handling reads (jsdom has no `DataTransfer` to construct in tests). */
export interface PasteClipboardData {
  readonly files: { readonly length: number };
  readonly items: Iterable<{ readonly kind: string; readonly type: string }>;
  getData(format: string): string;
}

export function clipboardHasImageOrFile(data: PasteClipboardData): boolean {
  if (data.files.length > 0) {
    return true;
  }
  return [...data.items].some(
    (item) => item.kind === "file" || item.type.startsWith("image/"),
  );
}

export function htmlHasInlineImageData(html: string): boolean {
  return /<img\b[^>]*\bsrc\s*=\s*["']?(?:data:image\/|blob:)/i.test(html);
}

export function htmlLooksLikeSpreadsheetPaste(html: string): boolean {
  // A spreadsheet paste is only meaningful when there is a real table to flatten
  // into tab-separated rows (Excel/Sheets clipboard HTML is always built around
  // one). We must NOT treat Microsoft Office markers (`mso-` CSS, the
  // office:excel namespace) as a spreadsheet on their own: Outlook stamps `mso-`
  // styles onto ordinary email prose, so matching them here routes plain Outlook
  // text down the spreadsheet path, where `spreadsheetHtmlToText` finds no rows,
  // returns "", and the caller falls back to Plate's default — collapsing the
  // very line breaks PRO-957 fixes (see {@link htmlToParagraphHtml}). Require an
  // actual table/row so non-tabular Outlook text falls through to that fix.
  return /<(?:table|tr|td|th)\b/i.test(html);
}

export function spreadsheetHtmlToText(html: string): string {
  const doc = new DOMParser().parseFromString(html, "text/html");
  for (const node of doc.querySelectorAll("style, script, meta, link")) {
    node.remove();
  }

  return Array.from(doc.querySelectorAll("tr"))
    .map((row) =>
      Array.from(row.querySelectorAll("td, th"))
        .map((cell) => cell.textContent?.trim() ?? "")
        .join("\t")
        .replace(/\s+$/u, ""),
    )
    .filter((row) => row.trim().length > 0)
    .join("\n");
}

/**
 * Rich block structures (lists, tables, headings, blockquotes, …) are
 * deserialized correctly by Plate's own plugins, so we leave them to the
 * default paste path. We only take over for the plain multi-paragraph case
 * Plate mishandles (see {@link htmlToParagraphHtml}).
 */
export function htmlHasComplexBlockStructure(html: string): boolean {
  return /<(ul|ol|li|table|thead|tbody|tr|td|th|blockquote|pre|h[1-6]|figure|hr)\b/i.test(
    html,
  );
}

/**
 * Split pasted HTML into one `<p>` per visual line/paragraph, preserving inline
 * formatting.
 *
 * Why this exists (PRO-957): Plate's HTML deserializer only maps `<p>` to a
 * paragraph block. Block elements it does not recognize — chiefly `<div>`,
 * which is what Gmail/Outlook and many web pages emit per line — are unwrapped
 * to their inline children, and a top-level `<br>` is turned into a literal
 * `"\n"`. When every top-level node ends up inline, Plate merges them into a
 * single paragraph, so multi-paragraph pastes collapse into one block.
 *
 * We rebuild explicit `<p>` boundaries from `<div>`/`<br>`/block boundaries
 * (keeping inline markup intact) so the re-deserialized fragment keeps its line
 * breaks and paragraph spacing. Empty source blocks / standalone `<br>`s become
 * empty paragraphs so blank-line spacing survives.
 *
 * Returns the `outerHTML` of each rebuilt paragraph. A result of length <= 1
 * means there was no multi-block structure to fix, and the caller should defer
 * to Plate's default paste (so e.g. inline paste into the middle of a line is
 * not turned into a paragraph break).
 */
/** Split plain-text clipboard content into one string per visual line (HELP-293). */
export function plainTextToParagraphLines(text: string): string[] {
  const lines = text.replace(/\r\n|\r/g, "\n").split("\n");

  let start = 0;
  while (start < lines.length && lines[start]?.trim() === "") {
    start++;
  }
  let end = lines.length - 1;
  while (end > start && lines[end]?.trim() === "") {
    end--;
  }

  return lines.slice(start, end + 1);
}

// Real tags only: a loose `<[a-z].*>` test also matches prose like "size <M>",
// routing plain text down the HTML path where its newlines collapse to a blob.
const HTML_TAG =
  /<\/?(?:p|br|div|span|a|b|i|u|em|strong|ul|ol|li|h[1-6]|blockquote|pre|code|table|tr|td|th|img|hr|figure|section|font)\b[^>]*>/i;

/** Whether a string is HTML markup rather than plain text that merely contains `<`. */
export function looksLikeHtml(text: string): boolean {
  return HTML_TAG.test(text);
}

// Rebuild paragraph boundaries in HTML Plate would otherwise collapse, so
// `<div>`/`<br>` bodies keep their line breaks instead of merging into one block.
export function normalizeHtmlBlockBoundaries(html: string): string {
  // Rich block structure (lists, tables, headings) deserializes correctly
  // through Plate's own plugins; flattening it would lose more than it saves.
  if (html.length === 0 || htmlHasComplexBlockStructure(html)) {
    return html;
  }
  const paragraphs = htmlToParagraphHtml(html);
  /** One paragraph means there was nothing multi-block to rebuild. */
  return paragraphs.length > 1 ? paragraphs.join("") : html;
}

export function htmlToParagraphHtml(html: string): string[] {
  const doc = new DOMParser().parseFromString(html, "text/html");
  for (const node of doc.querySelectorAll("style, script, meta, link, title")) {
    node.remove();
  }

  const paragraphs: HTMLElement[] = [];
  let current = doc.createElement("p");

  const commit = (allowEmpty: boolean): void => {
    if (current.childNodes.length > 0) {
      paragraphs.push(current);
      current = doc.createElement("p");
    } else if (allowEmpty) {
      paragraphs.push(current);
      current = doc.createElement("p");
    }
  };

  const walk = (node: Node): void => {
    for (const child of node.childNodes) {
      if (child.nodeType === Node.TEXT_NODE) {
        const text = child.textContent ?? "";
        if (text.trim().length === 0) {
          // Whitespace/newlines between block tags: only keep a separating
          // space when we are mid-line, never as standalone content.
          if (current.childNodes.length > 0) {
            current.append(doc.createTextNode(" "));
          }
        } else {
          current.append(doc.createTextNode(text));
        }
        continue;
      }

      if (child.nodeType !== Node.ELEMENT_NODE) {
        continue;
      }

      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      const element = child as HTMLElement;
      const tag = element.tagName;

      if (tag === "BR") {
        // A line break ends the current paragraph; allow an empty one so blank
        // lines (`<div><br></div>`) keep their spacing.
        commit(true);
        continue;
      }

      if (INLINE_TAGS.has(tag)) {
        current.append(element.cloneNode(true));
        continue;
      }

      // Block-level element (div, p, section, …): close the current line,
      // descend, then close again so the block's last line is its own
      // paragraph. A block that yields nothing becomes one empty paragraph.
      commit(false);
      const before = paragraphs.length;
      walk(element);
      if (current.childNodes.length === 0 && paragraphs.length === before) {
        paragraphs.push(doc.createElement("p"));
        current = doc.createElement("p");
      } else {
        commit(false);
      }
    }
  };

  walk(doc.body);
  commit(false);

  const isEmptyParagraph = (paragraph: HTMLElement | undefined): boolean =>
    paragraph !== undefined && paragraph.childNodes.length === 0;

  while (isEmptyParagraph(paragraphs[0])) {
    paragraphs.shift();
  }
  while (isEmptyParagraph(paragraphs[paragraphs.length - 1])) {
    paragraphs.pop();
  }

  return paragraphs.map((paragraph) => paragraph.outerHTML);
}
